import { useEffect, useRef } from 'react';
import { animate, useReducedMotion } from 'motion/react';
import { formatScore } from './util';

export interface CountUpProps {
  /** Target value. Changing it animates from the currently displayed value. */
  value: number;
  /** Start value on first mount (default 0). Pass `value` to skip the initial count. */
  from?: number;
  /** Seconds. */
  duration?: number;
  decimals?: number;
  /** Custom formatter (overrides `decimals`). */
  format?: (n: number) => string;
  /** Delay in seconds before counting. */
  delay?: number;
  onDone?: () => void;
  className?: string;
}

/**
 * Animated number (jury score reveal, totals). Writes text straight into the span on each frame so
 * React does not re-render 60×/s. Respects reduced motion (jumps to the value).
 */
export function CountUp({
  value,
  from = 0,
  duration = 1.1,
  decimals = 1,
  format,
  delay = 0,
  onDone,
  className,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef(from);
  const reduce = useReducedMotion();
  const fmt = useRef(format);
  const done = useRef(onDone);
  useEffect(() => {
    fmt.current = format;
    done.current = onDone;
  });

  useEffect(() => {
    const render = (n: number) => {
      const text = fmt.current ? fmt.current(n) : formatScore(n, decimals);
      const node = ref.current?.firstChild;
      // Mutate React's own text node so later reconciliation stays consistent.
      if (node && node.nodeType === Node.TEXT_NODE) node.nodeValue = text;
      else if (ref.current) ref.current.textContent = text;
    };
    if (reduce || duration <= 0) {
      shown.current = value;
      render(value);
      done.current?.();
      return;
    }
    const controls = animate(shown.current, value, {
      duration,
      delay,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (n) => {
        shown.current = n;
        render(n);
      },
      onComplete: () => done.current?.(),
    });
    return () => controls.stop();
  }, [value, duration, delay, decimals, reduce]);

  return (
    <span
      ref={ref}
      className={className}
      aria-label={formatScore(value, decimals)}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {format ? format(from) : formatScore(from, decimals)}
    </span>
  );
}
